// Ubicación en el repo: src/App.jsx
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { SessionProvider, useSession } from './lib/SessionContext';
import { ProfessionalProvider } from './lib/ProfessionalContext';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Patients from './pages/Patients';
import PatientDetail from './pages/PatientDetail';
import Agenda from './pages/Agenda';
import Exams from './pages/Exams';
import Prescriptions from './pages/Prescriptions';
import HearingAids from './pages/HearingAids';
import OftSales from './pages/OftSales';
import Settings from './pages/Settings';
import PublicScheduling from './pages/PublicScheduling';
import PublicSchedulingCancel from './pages/PublicSchedulingCancel';
import PublicLanding from './pages/PublicLanding';
import AurentecInfo from './pages/AurentecInfo';
import Spinner from './components/Spinner';

function LoadingScreen() {
  return (
    <div className="app-loading">
      <Spinner size="lg" />
    </div>
  );
}

// Redirect de compatibilidad para /pacientes/:id (mudanza a /app,
// 23-ago-2026) — preserva el id en vez de mandar genérico a /app/pacientes.
function RedirectPatientDetail() {
  const { id } = useParams();
  return <Navigate to={`/app/pacientes/${id}`} replace />;
}

// Protege rutas internas: sin sesión, con 2FA pendiente, o con enrolamiento
// de 2FA obligatorio sin resolver, manda a /login.
function RequireAuth({ children }) {
  const { status, session, mfaSatisfied, needsMfaEnrollment } = useSession();
  if (status === 'loading') return <LoadingScreen />;
  if (!session || !mfaSatisfied || needsMfaEnrollment) return <Navigate to="/login" replace />;
  return children;
}

// En /login: si ya hay sesión completa (2FA resuelto y, si el rol lo exige,
// ya enrolado), no tiene sentido seguir mostrando el formulario — se manda
// adentro. Si falta cualquiera de esos dos pasos, se queda en /login
// (Login.jsx decide cuál mostrar).
//
// Manda a /app, no a / (23-ago-2026, ver DECISIONS.md): desde que existe
// PublicLanding, la raíz del dominio pasó a ser la página pública
// institucional — el software interno vive en /app.
function RedirectIfAuthed({ children }) {
  const { status, session, mfaSatisfied, needsMfaEnrollment } = useSession();
  if (status === 'loading') return <LoadingScreen />;
  if (session && mfaSatisfied && !needsMfaEnrollment) return <Navigate to="/app" replace />;
  return children;
}

export default function App() {
  return (
    <SessionProvider>
      <ProfessionalProvider>
        <BrowserRouter>
          <Routes>
            {/* Página pública institucional (23-ago-2026, ver DECISIONS.md)
                — la raíz del dominio. Antes esto no existía y `/` caía
                directo bajo RequireAuth, así que cualquier visitante sin
                sesión terminaba en /login sin ningún contexto. */}
            <Route path="/" element={<PublicLanding />} />

            {/* Info de audífonos Aurentec (30-sep-2026, DECISIONS.md #128) —
                pública, sin sesión, mismo criterio que PublicLanding.
                Destino del logo de Aurentec en la sección de Alianzas. */}
            <Route path="/audifonos-aurentec" element={<AurentecInfo />} />

            <Route
              path="/login"
              element={
                <RedirectIfAuthed>
                  <Login />
                </RedirectIfAuthed>
              }
            />
            <Route path="/definir-password" element={<SetPassword />} />

            {/* Auto-agendamiento público (spec-final-autoagendamiento.md) —
                sin sesión, fuera de RequireAuth/Layout a propósito: un
                visitante externo nunca debe necesitar login para llegar
                acá. Cancelación por token (sección 7, DECISIONS.md #74) —
                antes de esta ruta, el link del correo caía en el catch-all
                de abajo y mandaba a cualquiera (incluido personal logueado)
                directo al software interno. */}
            <Route path="/agendamiento-online" element={<PublicScheduling />} />
            <Route path="/agendamiento-online/cancelar" element={<PublicSchedulingCancel />} />

            {/* Software interno, bajo /app (23-ago-2026, ver DECISIONS.md):
                antes vivía en la raíz — se mudó para dejarle el dominio
                raíz a PublicLanding. Rutas hijas relativas (sin "/" al
                inicio), heredan el prefijo del padre. */}
            <Route
              path="/app"
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="pacientes" element={<Patients />} />
              <Route path="pacientes/:id" element={<PatientDetail />} />
              <Route path="agenda" element={<Agenda />} />
              <Route path="audiometrias" element={<Exams />} />
              <Route path="audifonos" element={<HearingAids />} />
              <Route path="recetas" element={<Prescriptions />} />
              <Route path="ventas" element={<OftSales />} />
              <Route path="configuracion" element={<Settings />} />
              {/* Rutas antiguas (antes de agrupar todo bajo Configuración,
                  ver DECISIONS.md) — se dejan como redirect por si queda
                  algún link/marcador guardado apuntando ahí. */}
              <Route path="profesionales" element={<Navigate to="/app/configuracion?tab=profesionales" replace />} />
              <Route path="sucursales" element={<Navigate to="/app/configuracion?tab=sucursales" replace />} />
              <Route path="catalogo" element={<Navigate to="/app/configuracion?tab=catalogo" replace />} />
            </Route>

            {/* Rutas viejas de antes de la mudanza a /app (marcadores/links
                guardados) — se redirigen a su equivalente nuevo en vez de
                caer en el catch-all genérico. */}
            <Route path="/pacientes" element={<Navigate to="/app/pacientes" replace />} />
            <Route path="/pacientes/:id" element={<RedirectPatientDetail />} />
            <Route path="/agenda" element={<Navigate to="/app/agenda" replace />} />
            <Route path="/audiometrias" element={<Navigate to="/app/audiometrias" replace />} />
            <Route path="/audifonos" element={<Navigate to="/app/audifonos" replace />} />
            <Route path="/recetas" element={<Navigate to="/app/recetas" replace />} />
            <Route path="/ventas" element={<Navigate to="/app/ventas" replace />} />
            <Route path="/configuracion" element={<Navigate to="/app/configuracion" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ProfessionalProvider>
    </SessionProvider>
  );
}
